import { useState, useEffect, useMemo, useCallback } from 'react';
import { CommunicationService, sharedApiClient } from '../fetcher';
import { useNotifications, todayCalendarDate, calendarDateToEpochMillis, isAbortError } from '@medprompt/shared';
import { StatusEnum } from '../enums/statusEnum';

const useDocumentFileAway = ({ doc, documentService, updateDoc, getDocScopeId, docScopeOptions, loadKey }) => {
  const { addNotification } = useNotifications();
  const communicationService = useMemo(() => new CommunicationService(sharedApiClient), []);

  const [comms, setComms] = useState([]);
  const [fileAwayLoading, setFileAwayLoading] = useState(false);
  const [showFileAwayModal, setShowFileAwayModal] = useState(false);

  const docId = doc?.documentId;

  useEffect(() => {
    setComms([]);
    setShowFileAwayModal(false);
    setFileAwayLoading(false);
  }, [loadKey]);

  // Guarded by the doc scope so comms for a previous document never land on this one.
  const fetchComms = useCallback(async (targetDocumentId, requestId) => {
    try {
      const documentComms = await communicationService.fetchDocumentComms(targetDocumentId, docScopeOptions());
      if (requestId !== getDocScopeId()) {
        return;
      }
      setComms(documentComms || []);
    } catch (error) {
      if (isAbortError(error) || requestId !== getDocScopeId()) return;
      console.error('Failed to fetch document comms:', error);
      setComms([]);
    }
  }, [communicationService, docScopeOptions, getDocScopeId]);

  useEffect(() => {
    if (docId) {
      fetchComms(docId, getDocScopeId());
    } else {
      setComms([]);
    }
  }, [docId, fetchComms, getDocScopeId]);

  const handleFileAway = useCallback(async () => {
    if (!docId || fileAwayLoading) return;
    const requestId = getDocScopeId();

    // Fetch latest communications before showing the modal
    await fetchComms(docId, requestId);
    if (requestId !== getDocScopeId()) return;

    setShowFileAwayModal(true);
  }, [docId, fileAwayLoading, fetchComms, getDocScopeId]);

  const handleFileAwayConfirm = useCallback((fileAwayDate, shouldIncludeTasks, shouldIncludeComms, selectedCommentIds) => {
    if (!docId || fileAwayLoading) return;
    const targetDocumentId = docId;

    setFileAwayLoading(true);

    // Check if the selected date is today
    const todayString = todayCalendarDate();

    let dateParam = null;
    if (fileAwayDate !== todayString) {
      dateParam = calendarDateToEpochMillis(fileAwayDate);
    }

    console.log('Filing away with date:', fileAwayDate, 'todayString:', todayString, 'dateParam:', dateParam ?? 'null (today)', 'includeTasks:', shouldIncludeTasks, 'includeComms:', shouldIncludeComms, 'selectedCommentIds:', selectedCommentIds);

    documentService.fileAwayDocument(targetDocumentId, dateParam, shouldIncludeTasks, shouldIncludeComms, selectedCommentIds)
      .then(() => {
        // The response only confirms the document was queued, so reflect that locally rather than
        // waiting for the status event: it is what disables editing and a second File Away.
        updateDoc(prevDoc => (
          prevDoc && prevDoc.documentId === targetDocumentId
            ? { ...prevDoc, status: StatusEnum.QUEUED_FOR_FILING.value }
            : prevDoc
        ));
        setFileAwayLoading(false);
        setShowFileAwayModal(false);
      }).catch((error) => {
        console.error('Error filing away document:', error);
        addNotification(`file-away-error-${targetDocumentId}`, 'Failed to file away document', { isError: true });
        setFileAwayLoading(false);
        setShowFileAwayModal(false);
      });
  }, [docId, fileAwayLoading, documentService, updateDoc, addNotification]);

  const handleFileAwayCancel = useCallback(() => {
    setShowFileAwayModal(false);
  }, []);

  return {
    comms,
    fileAwayLoading,
    showFileAwayModal,
    handleFileAway,
    handleFileAwayConfirm,
    handleFileAwayCancel,
  };
};

export default useDocumentFileAway;
